import React from 'react';
import { 
  Star, 
  User, 
  Users, 
  MessageSquare, 
  Award,
  Save,
  X
} from 'lucide-react';

const EvaluationForm = ({
  formData,
  formErrors,
  selectedEvaluation,
  onInputChange,
  onSubmit,
  submitting,
  soutenances,
  criteres,
  enseignantsInternes,
  enseignantsExternes
}) => {
  const getNoteColor = (note) => {
    if (!note) return 'border-gray-300';
    if (note >= 16) return 'border-green-500 bg-green-50';
    if (note >= 14) return 'border-blue-500 bg-blue-50';
    if (note >= 12) return 'border-yellow-500 bg-yellow-50';
    if (note >= 10) return 'border-orange-500 bg-orange-50';
    return 'border-red-500 bg-red-50';
  };

  const getNoteLabel = (note) => {
    if (!note) return '';
    if (note >= 16) return 'Excellent';
    if (note >= 14) return 'Très bien';
    if (note >= 12) return 'Bien';
    if (note >= 10) return 'Assez bien';
    return 'Insuffisant';
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Informations sur la soutenance */}
      <div className="p-4 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <Users className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Soutenance</h3>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sélectionner une soutenance *
          </label>
          <select
            required
            name="soutenance"
            value={formData.soutenance}
            onChange={onInputChange}
            className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              formErrors.soutenance ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Sélectionner une soutenance</option>
            {soutenances.map(soutenance => (
              <option key={soutenance.id} value={soutenance.id}>
                {soutenance.etudiant_principal_info ? 
                  `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom} - ${soutenance.titre_travail?.substring(0, 30)}` :
                  soutenance.titre_travail
                }
              </option>
            ))}
          </select>
          {formErrors.soutenance && (
            <p className="mt-1 text-sm text-red-600">{formErrors.soutenance}</p>
          )}
        </div>
      </div>

      {/* Critère d'évaluation */}
      <div className="p-4 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <Award className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Critère d'évaluation</h3>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Critère *
          </label>
          <select
            required
            name="critere"
            value={formData.critere}
            onChange={onInputChange}
            disabled={!formData.soutenance}
            className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              formErrors.critere ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">
              {formData.soutenance ? 'Sélectionner un critère' : 'Sélectionnez d\'abord une soutenance'}
            </option>
            {criteres.length === 0 && formData.soutenance && (
              <option value="" disabled>Aucun critère disponible pour cette soutenance</option>
            )}
            {criteres.map(critere => (
              <option key={critere.id} value={critere.id}>
                {critere.nom} (Coeff: {critere.coefficient})
              </option>
            ))}
          </select>
          {!formData.soutenance && (
            <p className="mt-1 text-xs text-gray-500">Sélectionnez d'abord une soutenance pour voir les critères disponibles.</p>
          )}
          {formErrors.critere && (
            <p className="mt-1 text-sm text-red-600">{formErrors.critere}</p>
          )}
        </div>
      </div>

      {/* Évaluateur */}
      <div className="p-4 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <User className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Évaluateur</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Évaluateur interne
            </label>
            <select
              name="evaluateur_interne"
              value={formData.evaluateur_interne}
              onChange={onInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sélectionner un enseignant interne</option>
              {enseignantsInternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.utilisateur?.first_name} {enseignant.utilisateur?.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Évaluateur externe
            </label>
            <select
              name="evaluateur_externe"
              value={formData.evaluateur_externe}
              onChange={onInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sélectionner un enseignant externe</option>
              {enseignantsExternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.nom_complet}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {(!formData.evaluateur_interne && !formData.evaluateur_externe) && (
          <p className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded-lg">
            ⚠️ Veuillez sélectionner au moins un évaluateur (interne ou externe)
          </p>
        )}
      </div>

      {/* Note et évaluation */}
      <div className="p-4 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <Star className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Note et évaluation</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (/20) *
            </label>
            <div className="relative">
              <input
                type="number"
                required
                min="0"
                max="20"
                step="0.5"
                name="note"
                value={formData.note}
                onChange={onInputChange}
                className={`w-full border rounded-lg px-4 py-3 pr-12 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${getNoteColor(formData.note)} ${
                  formErrors.note ? 'border-red-500' : ''
                }`}
                placeholder="15.5"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Star className="h-5 w-5 text-blue-500" />
              </div>
            </div>
            {formData.note && (
              <p className="mt-1 text-sm font-medium text-gray-600">
                {getNoteLabel(formData.note)}
              </p>
            )}
            {formErrors.note && (
              <p className="mt-1 text-sm text-red-600">{formErrors.note}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Commentaire
            </label>
            <textarea
              name="commentaire"
              value={formData.commentaire}
              onChange={onInputChange}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Observations, commentaires, recommandations..."
            />
            {formErrors.commentaire && (
              <p className="mt-1 text-sm text-red-600">{formErrors.commentaire}</p>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <X className="h-4 w-4" />
          <span>Annuler</span>
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Enregistrement...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>{selectedEvaluation ? 'Modifier' : 'Créer'} l'évaluation</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default EvaluationForm; 